/* eslint-disable react-hooks/exhaustive-deps */
import {FC, useContext, useState, useEffect, createContext} from 'react'
import {useQuery} from 'react-query'
import {
  initialQueryResponse,
  PaginationState,
  QUERIES,
  WithChildren,
  QueryResponseContextProps,
} from '../../../../../_metronic/helpers'
import {getProjectsList} from './_requests'
import {Project, ProjectsQueryResponse} from './_models'
import {useSearchParams} from 'react-router-dom'

// Estende o tipo do contexto para incluir totalProjectsInInstance
type ExtendedQueryResponseContextProps<T> = QueryResponseContextProps<T> & {
  totalProjectsInInstance?: number | null
}

const initialExtendedQueryResponse: ExtendedQueryResponseContextProps<Project> = {
  ...initialQueryResponse,
  totalProjectsInInstance: null,
}

const QueryResponseContext = createContext<ExtendedQueryResponseContextProps<Project>>(initialExtendedQueryResponse)
const QueryResponseProvider: FC<WithChildren> = ({children}) => {
  const [searchParams] = useSearchParams()
  const [query, setQuery] = useState<string>(searchParams.toString())
  const [isEmptyResults, setIsEmptyResults] = useState(false)
  const [totalProjectsInInstance, setTotalProjectsInInstance] = useState<number | null>(null) // null = ainda não carregou

  useEffect(() => {
    setQuery(searchParams.toString())
  }, [searchParams])

  const hasParams = query !== ''

  const {
    isLoading: queryIsLoading,
    refetch,
    data: response,
  } = useQuery(`${QUERIES.PROJECTS_LIST}-${query}`, () => getProjectsList(query), {
    enabled: hasParams, // evita chamada inicial sem filtros / parâmetros
    onSuccess: (data) => {
      setIsEmptyResults(data.isEmptyResults)
      setTotalProjectsInInstance(data.totalProjectsInInstance ?? null)
    },
    cacheTime: 0,
    keepPreviousData: true,
    refetchOnWindowFocus: false,
  })

  // Enquanto não há parâmetros (URL vazia), consideramos como "carregando"
  const isLoading = !hasParams || queryIsLoading

  return (
    <QueryResponseContext.Provider
      value={{isLoading, refetch, response: response?.data, query, isEmptyResults, totalProjectsInInstance}}
    >
      {children}
    </QueryResponseContext.Provider>
  )
}

const useQueryResponse = (): ExtendedQueryResponseContextProps<Project> => useContext(QueryResponseContext)

const useQueryResponseData = () => {
  const {response} = useQueryResponse()

  if (!response) return []

  return response?.data || []
}

const useQueryResponsePagination = () => {
  const defaultPaginationState: PaginationState = {
    links: [],
    per_page: 12,
    current_page: 1,
    // ...initialQueryState,
  }

  const {response} = useQueryResponse()
  if (!response || !response.payload || !response.payload.pagination) {
    return defaultPaginationState
  }

  return response.payload.pagination
}

const useQueryResponseLoading = (): boolean => {
  const {isLoading} = useQueryResponse()
  return isLoading
}

const useQueryResponseTotalData = (): number => {
  const {response} = useQueryResponse()
  return response?.payload?.pagination?.total ?? 0
}

const useQueryResponseIsEmptyResults = (): boolean => {
  const {isEmptyResults} = useQueryResponse()
  return isEmptyResults ?? false
}

const useQueryResponseTotalProjectsInInstance = (): number | null => {
  const {totalProjectsInInstance} = useQueryResponse()
  return totalProjectsInInstance ?? null
}

export {
  QueryResponseProvider,
  useQueryResponse,
  useQueryResponseData,
  useQueryResponsePagination,
  useQueryResponseLoading,
  useQueryResponseTotalData,
  useQueryResponseIsEmptyResults,
  useQueryResponseTotalProjectsInInstance,
}
